'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type LangCode =
  | 'en' | 'es' | 'pt' | 'fr' | 'de' | 'zh' | 'ja' | 'ko' |'it'| 'nl' | 'pl' | 'tr' | 'ru' | 'uk' | 'sv' | 'no' |'da'| 'fi' | 'el' | 'cs' | 'hu' | 'ro' |'zh-tw'| 'yue' | 'id' | 'vi' | 'th' | 'ms' | 'fil' | 'fa' | 'ar' | 'hi' | 'bn' | 'ur';

export interface Translations {
  // Nav
  nav_home: string;
  nav_extractor: string;
  nav_remover: string;
  nav_help: string;
  nav_contact: string;
  // Hero
  hero_badge_instant: string;
  hero_badge_deleted: string;
  hero_badge_free: string;
  hero_title_1: string;
  hero_title_2: string;
  hero_title_3: string;
  hero_subtitle: string;
  hero_tab_extractor: string;
  hero_tab_remover: string;
  // Format section
  formats_title: string;
  formats_subtitle: string;
  formats_video_title: string;
  formats_video_subtitle: string;
  formats_audio_title: string;
  formats_audio_subtitle: string;
  formats_max_size: string;
  formats_popular: string;
  // FAQ
  faq_badge: string;
  faq_title: string;
  faq_subtitle: string;
  faq_cat_all: string;
  // Footer
  footer_desc: string;
  footer_output_formats: string;
  footer_privacy_notice_title: string;
  footer_privacy_notice: string;
  footer_tools_title: string;
  footer_support_title: string;
  footer_copyright: string;
  footer_all_rights: string;
  footer_popular_searches_label: string;
  footer_popular_searches: string;
  // Language modal
  lang_modal_title: string;
  lang_modal_count: string;
  lang_search_placeholder: string;
  lang_no_results: string;
  // Tool section
  tool_extractor_title: string;
  tool_extractor_subtitle: string;
  tool_remover_title: string;
  tool_remover_subtitle: string;
  // Audio Remover Tool
  remover_info_title: string;
  remover_info_desc: string;
  remover_before_label: string;
  remover_after_label: string;
  remover_client_side: string;
  remover_drop_active: string;
  remover_upload_prompt: string;
  remover_upload_hint: string;
  remover_browse_files: string;
  remover_choose_different: string;
  remover_btn_remove: string;
  remover_privacy_note: string;
  remover_processing_title: string;
  remover_processing_keep_open: string;
  remover_stage_stripping: string;
  remover_stage_processing: string;
  remover_stage_finalizing: string;
  remover_stage_loading: string;
  remover_stage_reading: string;
  remover_stage_removing: string;
  remover_stage_output: string;
  remover_stage_complete: string;
  remover_stage_preparing: string;
  remover_stage_initializing: string;
  remover_success_title: string;
  remover_success_desc: string;
  remover_silent_label: string;
  remover_btn_download: string;
  remover_btn_reset: string;
  remover_browser_note: string;
  // Audio Extractor Tool
  extractor_output_format_title: string;
  extractor_output_format_desc: string;
  extractor_format_guide: string;
  extractor_quality_title: string;
  extractor_quality_desc: string;
  extractor_quality_est: string;
  extractor_drop_active: string;
  extractor_upload_prompt: string;
  extractor_upload_hint: string;
  extractor_browse_files: string;
  extractor_choose_different: string;
  extractor_btn_extract: string;
  extractor_privacy_note: string;
  extractor_processing_title: string;
  extractor_processing_keep_open: string;
  extractor_success_title: string;
  extractor_success_desc: string;
  extractor_btn_download: string;
  extractor_btn_reset: string;
  extractor_autodelete_note: string;
  extractor_error_title: string;
  extractor_upload_error_file: string;
  extractor_upload_error_size: string;
}
// ... existing code ...

const translations: Record<LangCode, Translations> = {
  en: {
    nav_home: 'Home',
    nav_extractor: 'Audio Extractor',
    nav_remover: 'Audio Remover',
    nav_help: 'Help',
    nav_contact: 'Contact',
    hero_badge_instant: 'Instant Processing',
    hero_badge_deleted: 'Files Auto-Deleted',
    hero_badge_free: 'Free Forever',
    hero_title_1: 'Extract Audio From',
    hero_title_2: 'Any Video',
    hero_title_3: 'Instantly',
    hero_subtitle: 'Convert video to MP3, WAV, FLAC and more using our free online audio extractor. Or remove audio completely from any video — no account needed.',
    hero_tab_extractor: 'Audio Extractor',
    hero_tab_remover: 'Audio Remover',
    formats_title: 'Supported Formats',
    formats_subtitle: 'All popular video input formats and 9 audio output formats supported',
    formats_video_title: 'Video Input Formats',
    formats_video_subtitle: 'Upload videos in these formats',
    formats_audio_title: 'Audio Output Formats',
    formats_audio_subtitle: 'Extract to any of these formats',
    formats_max_size: 'Maximum file size:',
    formats_popular: 'POPULAR',
    faq_badge: 'Frequently Asked Questions',
    faq_title: 'Everything you need to know',
    faq_subtitle: 'Common questions about audio extraction, audio removal, and file formats',
    faq_cat_all: 'All',
    footer_desc: 'Free online tools to extract audio from videos and remove audio from video files quickly and easily. No account required. No watermarks. No limits.',
    footer_output_formats: 'Supported Output Formats',
    footer_privacy_notice_title: 'Your privacy is protected.',
    footer_privacy_notice: 'All uploaded files are processed securely and automatically deleted after processing. We never store your files long-term.',
    footer_tools_title: 'Tools & Pages',
    footer_support_title: 'Support & Legal',
    footer_copyright: 'Free audio extractor and audio remover online.',
    footer_all_rights: 'All rights reserved.',
    footer_popular_searches_label: 'Popular searches:',
    footer_popular_searches: 'video to mp3 free · audio extractor online free · remove audio from video · audio remover free',
    lang_modal_title: 'Select Language',
    lang_modal_count: 'languages available',
    lang_search_placeholder: 'Search languages...',
    lang_no_results: 'No languages match',
    tool_extractor_title: 'Audio Extractor',
    tool_extractor_subtitle: 'Extract audio from video — free online audio extractor',
    tool_remover_title: 'Audio Remover',
    tool_remover_subtitle: 'Remove audio from video — mute video online for free',
    remover_info_title: 'Remove Audio From Video',
    remover_info_desc: "Upload your video and we'll strip the audio track completely. The result is a silent video file — perfect for adding your own music or voiceover.",
    remover_before_label: 'Video with audio',
    remover_after_label: 'Silent video file',
    remover_client_side: 'Audio tracks removed client-side',
    remover_drop_active: 'Drop your video here',
    remover_upload_prompt: 'Upload your video or drag and drop file here',
    remover_upload_hint: 'MP4, MOV, AVI, MKV, WEBM · Max 500 MB',
    remover_browse_files: 'Browse Files',
    remover_choose_different: 'Choose a different file',
    remover_btn_remove: 'Remove Audio',
    remover_privacy_note: 'Files are processed entirely in your browser. Nothing is uploaded to any server.',
    remover_processing_title: 'Processing your file...',
    remover_processing_keep_open: 'Processing happens in your browser. Please keep this tab open.',
    remover_stage_stripping: 'Stripping audio track...',
    remover_stage_processing: 'Processing video stream...',
    remover_stage_finalizing: 'Finalizing silent video...',
    remover_stage_loading: 'Loading FFmpeg engine...',
    remover_stage_reading: 'Reading video file...',
    remover_stage_removing: 'Removing audio track...',
    remover_stage_output: 'Reading output...',
    remover_stage_complete: 'Complete!',
    remover_stage_preparing: 'Preparing...',
    remover_stage_initializing: 'Initializing...',
    remover_success_title: 'Audio Removed Successfully!',
    remover_success_desc: 'Your silent video is ready. The audio track has been completely stripped from the file.',
    remover_silent_label: 'Silent · No audio track',
    remover_btn_download: 'Download Silent Video',
    remover_btn_reset: 'Remove Audio From Another File',
    remover_browser_note: '🔒 Your file was processed entirely in your browser — no data was sent to any server.',
    extractor_output_format_title: 'Output Format',
    extractor_output_format_desc: 'Select the audio format for your extracted file',
    extractor_format_guide: 'Format guide',
    extractor_quality_title: 'Audio Quality',
    extractor_quality_desc: 'Higher quality = larger file size',
    extractor_quality_est: 'Est. output:',
    extractor_drop_active: 'Drop your video here',
    extractor_upload_prompt: 'Upload your video or drag and drop file here',
    extractor_upload_hint: 'Supports MP4, MOV, AVI, MKV, WEBM · Max 500 MB',
    extractor_browse_files: 'Browse Files',
    extractor_choose_different: 'Choose a different file',
    extractor_btn_extract: 'Extract Audio',
    extractor_privacy_note: 'Your file is processed securely and automatically deleted after extraction. We never store your files.',
    extractor_processing_title: 'Processing your file...',
    extractor_processing_keep_open: 'Processing time depends on file size. Please keep this tab open.',
    extractor_success_title: 'Extraction Complete!',
    extractor_success_desc: 'Your audio has been extracted and is ready to download.',
    extractor_btn_download: 'Download',
    extractor_btn_reset: 'Extract Another File',
    extractor_autodelete_note: '🔒 Your file will be automatically deleted from our servers within 1 hour.',
    extractor_error_title: 'Upload Error',
    extractor_upload_error_file: 'Please upload a video file first.',
    extractor_upload_error_size: 'File too large. Maximum upload size is 500 MB.',
  },
  es: {
    nav_home: 'Inicio',
    nav_extractor: 'Extractor de Audio',
    nav_remover: 'Eliminador de Audio',
    nav_help: 'Ayuda',
    nav_contact: 'Contacto',
    hero_badge_instant: 'Procesamiento Instantáneo',
    hero_badge_deleted: 'Archivos Eliminados Automáticamente',
    hero_badge_free: 'Gratis Para Siempre',
    hero_title_1: 'Extrae Audio De',
    hero_title_2: 'Cualquier Video',
    hero_title_3: 'Instantáneamente',
    hero_subtitle: 'Convierte video a MP3, WAV, FLAC y más con nuestro extractor de audio gratuito. O elimina el audio completamente de cualquier video — sin cuenta.',
    hero_tab_extractor: 'Extractor de Audio',
    hero_tab_remover: 'Eliminador de Audio',
    formats_title: 'Formatos Compatibles',
    formats_subtitle: 'Todos los formatos de video populares y 9 formatos de audio de salida',
    formats_video_title: 'Formatos de Video de Entrada',
    formats_video_subtitle: 'Sube videos en estos formatos',
    formats_audio_title: 'Formatos de Audio de Salida',
    formats_audio_subtitle: 'Extrae a cualquiera de estos formatos',
    formats_max_size: 'Tamaño máximo de archivo:',
    formats_popular: 'POPULAR',
    faq_badge: 'Preguntas Frecuentes',
    faq_title: 'Todo lo que necesitas saber',
    faq_subtitle: 'Preguntas comunes sobre extracción de audio, eliminación de audio y formatos de archivo',
    faq_cat_all: 'Todos',
    footer_desc: 'Herramientas gratuitas en línea para extraer audio de videos y eliminar audio de archivos de video de forma rápida y sencilla. Sin cuenta. Sin marcas de agua. Sin límites.',
    footer_output_formats: 'Formatos de Salida Compatibles',
    footer_privacy_notice_title: 'Tu privacidad está protegida.',
    footer_privacy_notice: 'Todos los archivos subidos se procesan de forma segura y se eliminan automáticamente después del procesamiento. Nunca almacenamos tus archivos a largo plazo.',
    footer_tools_title: 'Herramientas y Páginas',
    footer_support_title: 'Soporte y Legal',
    footer_copyright: 'Extractor y eliminador de audio en línea gratuito.',
    footer_all_rights: 'Todos los derechos reservados.',
    footer_popular_searches_label: 'Búsquedas populares:',
    footer_popular_searches: 'video a mp3 gratis · extractor de audio online gratis · eliminar audio de video · eliminador de audio gratis',
    lang_modal_title: 'Seleccionar Idioma',
    lang_modal_count: 'idiomas disponibles',
    lang_search_placeholder: 'Buscar idiomas...',
    lang_no_results: 'Ningún idioma coincide con',
    tool_extractor_title: 'Extractor de Audio',
    tool_extractor_subtitle: 'Extrae audio de video — gratuito extractor de audio en línea',
    tool_remover_title: 'Eliminador de Audio',
    tool_remover_subtitle: 'Elimina audio de video — silencia video en línea gratis',
    remover_info_title: 'Eliminar Audio del Video',
    remover_info_desc: 'Sube tu video y eliminaremos la pista de audio completamente. El resultado es un archivo de video silencioso — perfecto para agregar tu propia música o voz en off.',
    remover_before_label: 'Video con audio',
    remover_after_label: 'Video silencioso',
    remover_client_side: 'Pistas de audio eliminadas en el cliente',
    remover_drop_active: 'Suelta tu video aquí',
    remover_upload_prompt: 'Sube tu video o arrastra y suelta el archivo aquí',
    remover_upload_hint: 'MP4, MOV, AVI, MKV, WEBM · Max 500 MB',
    remover_browse_files: 'Explorar Archivos',
    remover_choose_different: 'Elegir un archivo diferente',
    remover_btn_remove: 'Eliminar Audio',
    remover_privacy_note: 'Los archivos son procesados completamente en tu navegador. Nada se sube a ningún servidor.',
    remover_processing_title: 'Procesando tu archivo...',
    remover_processing_keep_open: 'El procesamiento ocurre en tu navegador. Por favor mantén esta pestaña abierta.',
    remover_stage_stripping: 'Eliminando pista de audio...',
    remover_stage_processing: 'Procesando flujo de video...',
    remover_stage_finalizing: 'Finalizando video silencioso...',
    remover_stage_loading: 'Cargando motor FFmpeg...',
    remover_stage_reading: 'Leyendo archivo de video...',
    remover_stage_removing: 'Removiendo pista de audio...',
    remover_stage_output: 'Leyendo salida...',
    remover_stage_complete: '¡Completo!',
    remover_stage_preparing: 'Preparando...',
    remover_stage_initializing: 'Inicializando...',
    remover_success_title: '¡Audio Eliminado Exitosamente!',
    remover_success_desc: 'Tu video silencioso está listo. La pista de audio ha sido completamente eliminada del archivo.',
    remover_silent_label: 'Silencioso · Sin pista de audio',
    remover_btn_download: 'Descargar Video Silencioso',
    remover_btn_reset: 'Eliminar Audio de Otro Archivo',
    remover_browser_note: '🔒 Tu archivo fue procesado completamente en tu navegador — ningún dato fue enviado a ningún servidor.',
    extractor_output_format_title: 'Formato de Salida',
    extractor_output_format_desc: 'Selecciona el formato de audio para tu archivo extraído',
    extractor_format_guide: 'Guía de formatos',
    extractor_quality_title: 'Calidad de Audio',
    extractor_quality_desc: 'Mayor calidad = mayor tamaño de archivo',
    extractor_quality_est: 'Salida est.:',
    extractor_drop_active: 'Suelta tu video aquí',
    extractor_upload_prompt: 'Sube tu video o arrastra y suelta el archivo aquí',
    extractor_upload_hint: 'Compatible con MP4, MOV, AVI, MKV, WEBM · Max 500 MB',
    extractor_browse_files: 'Explorar Archivos',
    extractor_choose_different: 'Elegir un archivo diferente',
    extractor_btn_extract: 'Extraer Audio',
    extractor_privacy_note: 'Tu archivo se procesa de forma segura y se elimina automáticamente después de la extracción. Nunca almacenamos tus archivos.',
    extractor_processing_title: 'Procesando tu archivo...',
    extractor_processing_keep_open: 'El tiempo de procesamiento depende del tamaño del archivo. Por favor mantén esta pestaña abierta.',
    extractor_success_title: 'Extracción Completa!',
    extractor_success_desc: 'Tu audio ha sido extraído y está listo para descargar.',
    extractor_btn_download: 'Descargar',
    extractor_btn_reset: 'Extraer Otro Archivo',
    extractor_autodelete_note: '🔒 Tu archivo será eliminado automáticamente de nuestros servidores en 1 hora.',
  },
  de: {
    nav_home: 'Startseite',
    nav_extractor: 'Audio-Extraktor',
    nav_remover: 'Audio-Entferner',
    nav_help: 'Hilfe',
    nav_contact: 'Kontakt',
    hero_badge_instant: 'Sofortverarbeitung',
    hero_badge_deleted: 'Dateien Automatisch Gelöscht',
    hero_badge_free: 'Für Immer Kostenlos',
    hero_title_1: 'Audio Extrahieren Aus',
    hero_title_2: 'Jedem Video',
    hero_title_3: 'Sofort',
    hero_subtitle: 'Konvertieren Sie video in MP3, WAV, FLAC und mehr mit unserer kostenlosen Online-Audio-Extraktor. Eller entfernen Sie Audio vollständig aus jedem Video — kein Konto erforderlich.',
    hero_tab_extractor: 'Audio-Extraktor',
    hero_tab_remover: 'Audio-Entferner',
    formats_title: 'Unterstützte Formate',
    formats_subtitle: 'Alle gängigen Videoindataformate und 9 Audio-Ausgabeformate stöten',
    formats_video_title: 'Videoindataformate',
    formats_video_subtitle: 'Laden Sie Videos in diese Formate hoch',
    formats_audio_title: 'Audio-Ausgabeformate',
    formats_audio_subtitle: 'Extrahieren Sie zu einem dieser Formate',
    formats_max_size: 'Maximale Dateigröße:',
    formats_popular: 'BELIEBT',
    faq_badge: 'Häufig Gestellte Fragen',
    faq_title: 'Alles, was Sie wissen müssen',
    faq_subtitle: 'Häufige Fragen zur Audio-Extraktion, Audio-Entfernung und Dateiformaten',
    faq_cat_all: 'Alle',
    footer_desc: 'Kostenlose Online-Tools zum Extrahieren von Audio aus Videos und Entfernen von Audio aus Videodateien schnell und einfach. Kein Konto erforderlich. Keine Wasserzeichen. Keine Begrenzungen.',
    footer_output_formats: 'Unterstützte Ausgabeformate',
    footer_privacy_notice_title: 'Ihre Privatsphäre ist geschützt.',
    footer_privacy_notice: 'Alle hochgeladenen Dateien werden sicher verarbeitet und nach der Verarbeitung automatisch gelöscht. Wir speichern Ihre Dateien niemals langfristig.',
    footer_tools_title: 'Tools und Seiten',
    footer_support_title: 'Support & Rechtliches',
    footer_copyright: 'Kostenloser Online-Audio-Extraktor und -Entferner.',
    footer_all_rights: 'Alle Rechte vorbehalten.',
    footer_popular_searches_label: 'Beliebte Suchanfragen:',
    footer_popular_searches: 'video in mp3 kostenlos · audio extraktor online kostenlos · audio aus video entfernen · audio entferner kostenlos',
    lang_modal_title: 'Sprache Auswählen',
    lang_modal_count: 'Sprachen verfügbar',
    lang_search_placeholder: 'Sprachen suchen...',
    lang_no_results: 'Keine Sprachen kommen überein',
  },
  zh: {
    nav_home: '首页',
    nav_extractor: '音频提取器',
    nav_remover: '音频移除器',
    nav_help: '帮助',
    nav_contact: '联系我们',
    hero_badge_instant: '即时处理',
    hero_badge_deleted: '文件自动删除',
    hero_badge_free: '永久免费',
    hero_title_1: '从',
    hero_title_2: '任何视频',
    hero_title_3: '中提取音频',
    hero_subtitle: '使用我们的免费在线音频提取器将视频转换为MP3、WAV、FLAC等格式。或从任何视频中完全删除音频——无需账户。',
    hero_tab_extractor: '音频提取器',
    hero_tab_remover: '音频移除器',
    formats_title: '支持的格式',
    formats_subtitle: '支持所有流行的视频输入格式和9种音频输出格式',
    formats_video_title: '视频输入格式',
    formats_video_subtitle: '以这些格式上传视频',
    formats_audio_title: '音频输出格式',
    formats_audio_subtitle: '提取为以下任意格式',
    formats_max_size: '最大文件大小：',
    formats_popular: '热门',
    faq_badge: '常见问题',
    faq_title: '您需要了解的一切',
    faq_subtitle: '关于音频提取、音频删除和文件格式的常见问题',
    faq_cat_all: '全部',
    footer_desc: '免费在线工具，快速轻松地从视频中提取音频并从视频文件中删除音频。无需账户。无水印。无限制。',
    footer_output_formats: '支持的输出格式',
    footer_privacy_notice_title: '您的隐私受到保护。',
    footer_privacy_notice: '所有上传的文件都经过安全处理，并在处理后自动删除。我们从不长期存储您的文件。',
    footer_tools_title: '工具和页面',
    footer_support_title: '支持和法律',
    footer_copyright: '免费在线音频提取器和移除器。',
    footer_all_rights: '版权所有。',
    footer_popular_searches_label: '热门搜索：',
    footer_popular_searches: '视频转mp3免费 · 在线音频提取器免费 · 从视频中删除音频 · 音频移除器免费',
    lang_modal_title: '选择语言',
    lang_modal_count: '种语言可用',
    lang_search_placeholder: '搜索语言...',
    lang_no_results: '没有语言匹配',
  },
  ja: {
    nav_home: 'ホーム',
    nav_extractor: '音声抽出ツール',
    nav_remover: '音声削除ツール',
    nav_help: 'ヘルプ',
    nav_contact: 'お問い合わせ',
    hero_badge_instant: '即時処理',
    hero_badge_deleted: 'ファイル自動削除',
    hero_badge_free: '永久無料',
    hero_title_1: '動画から音声を',
    hero_title_2: '瞬時に',
    hero_title_3: '抽出',
    hero_subtitle: '使用する無料のオンライン音声抽出ツールで動画をMP3、WAV、FLAC等に変換。または動画から音声を完全に削除 — アカウント不要。',
    hero_tab_extractor: '音声抽出ツール',
    hero_tab_remover: '音声削除ツール',
    formats_title: '対応フォーマット',
    formats_subtitle: '人気の動画入力フォーマットと9つの音声出力フォーマットに対応',
    formats_video_title: '動画入力フォーマット',
    formats_video_subtitle: 'これらのフォーマットで動画をアップロード',
    formats_audio_title: '音声出力フォーマット',
    formats_audio_subtitle: 'これらのフォーマットに抽出',
    formats_max_size: '最大ファイルサイズ：',
    formats_popular: '人気',
    faq_badge: 'よくある質問',
    faq_title: '知っておくべきすべて',
    faq_subtitle: '音声抽出、音声削除、ファイル形式に関するよくある質問',
    faq_cat_all: 'すべて',
    footer_desc: '無料のオンライン音声抽出＆削除ツール。アカウント不要。透かしなし。制限なし。',
    footer_output_formats: '対応出力フォーマット',
    footer_privacy_notice_title: 'プライバシーは保護されています。',
    footer_privacy_notice: 'アップロードされたすべてのファイルは安全に処理され、処理後に自動的に削除されます。ファイルを長期間保存することはありません。',
    footer_tools_title: 'ツールとページ',
    footer_support_title: 'サポートと法的情報',
    footer_copyright: '無料オンライン音声抽出・削除ツール。',
    footer_all_rights: '全著作権所有。',
    footer_popular_searches_label: '人気の検索：',
    footer_popular_searches: '動画をmp3に無料変換 · オンライン音声抽出ツール無料 · 動画から音声を削除 · 音声削除ツール無料',
    lang_modal_title: '言語を選択',
    lang_modal_count: '言語が利用可能',
    lang_search_placeholder: '言語を検索...',
    lang_no_results: '一致する言語がありません',
  },
  ko: {
    nav_home: '홈',
    nav_extractor: '오디오 추출기',
    nav_remover: '오디오 제거기',
    nav_help: '도움말',
    nav_contact: '연락처',
    hero_badge_instant: '즉시 처리',
    hero_badge_deleted: '파일 자동 삭제',
    hero_badge_free: '영구 무료',
    hero_title_1: '모든 동영상에서',
    hero_title_2: '오디오 추출',
    hero_title_3: '즉시',
    hero_subtitle: '무료 온라인 오디오 추출기로 동영상을 MP3, WAV, FLAC 등으로 변환。 또는 동영상에서 오디오를 완전히 제거 — 계정 불필요.',
    hero_tab_extractor: '오디오 추출기',
    hero_tab_remover: '오디오 제거기',
    formats_title: '지원 형식',
    formats_subtitle: '모든 인기 동영상 입력 형식과 9가지 오디오 출력 형식 지원',
    formats_video_title: '동영상 입력 형식',
    formats_video_subtitle: '이 형식으로 동영상 업로드',
    formats_audio_title: '오디오 출력 형식',
    formats_audio_subtitle: '이 형식 중 하나로 추출',
    formats_max_size: '최대 파일 크기:',
    formats_popular: '인기',
    faq_badge: '자주 묻는 질문',
    faq_title: '알아야 할 모든 것',
    faq_subtitle: '오디오 추출, 오디오 제거 및 파일 형식에 관한 일반적인 질문',
    faq_cat_all: '전체',
    footer_desc: '무료 온라인 도구로서 영상에서 오디오를 추출하거나 영상 파일에서 오디오를 제거하는 작업을 빠르고 쉽게 할 수 있습니다. 계정 필요 없음. 워터마크 없음. 제한 없음.',
    footer_output_formats: '지원하는 출력 포맷',
    footer_privacy_notice_title: '개인정보는 보호됩니다.',
    footer_privacy_notice: '모든 업로드된 파일은 안전하게 처리되며, 처리 후 자동으로 삭제됩니다. 장기간 파일을 저장하지 않습니다.',
    footer_tools_title: '도구 및 페이지',
    footer_support_title: '지원 & 법적 정보',
    footer_copyright: '무료 온라인 오디오 추출기와 제거기.',
    footer_all_rights: '모든 권리 보유.',
    footer_popular_searches_label: '인기 검색어:',
    footer_popular_searches: '영상을 mp3로 무료 · 온라인 오디오 추출기 무료 · 영상에서 오디오 삭제 · 오디오 제거기 무료',
    lang_modal_title: '언어 선택',
    lang_modal_count: '사용 가능한 언어',
    lang_search_placeholder: '언어 검색...',
    lang_no_results: '일치하는 언어 없음',
  },
  id: {
    nav_home: 'Beranda',
    nav_extractor: 'Ekstraktor Audio',
    nav_remover: 'Penghapus Audio',
    nav_help: 'Bantuan',
    nav_contact: 'Kontak',
    hero_badge_instant: 'Pemprosesan Segera',
    hero_badge_deleted: 'Fail Dihapus Automatik',
    hero_badge_free: 'Chiniyelewa Kwa Mdaarani',
    hero_title_1: 'Ekstrak Audio Dari',
    hero_title_2: 'Video Apapun',
    hero_title_3: 'Tatapun',
    hero_subtitle: 'Konversi video ke MP3, WAV, FLAC dan lebih banyak dengan ekstraktor audio online gratis kami. Atau hapus audio sepenuhnya dari video apapun — tanpa akun.',
    hero_tab_extractor: 'Ekstraktor Audio',
    hero_tab_remover: 'Penghapus Audio',
    formats_title: 'Format yang Didukung',
    formats_subtitle: 'Format input video populer dan 9 format output audio didukung',
    formats_video_title: 'Định dạng đầu vào video',
    formats_video_subtitle: 'Tải lên video theo các định dạng này',
    formats_audio_title: 'Định dạng output audio',
    formats_audio_subtitle: 'Trích xuất thành một trong số các định dạng này',
    formats_max_size: 'Kích thước tệp tối đa:',
    formats_popular: 'PHỔ BIẾN',
    faq_badge: 'Câu hỏi thường gặp',
    faq_title: 'Tất cả những gì bạn cần biết',
    faq_subtitle: 'Câu hỏi phổ biến về trích xuất âm thanh, xóa âm thanh và định dạng tệp',
    faq_cat_all: 'Tất cả',
    footer_desc: 'Công cụ online miễn phí để trích xuất âm thanh từ video và xóa âm thanh khỏi tệp video nhanh và dễ dàng. Không cần tài khoản. Không có hình mờ. Không giới hạn.',
    footer_output_formats: 'Các định dạng đầu ra được hỗ trợ',
    footer_privacy_notice_title: 'Quyền riêng tư của bạn được bảo vệ.',
    footer_privacy_notice: 'Tất cả các tệp được tải lên được xử lý an toàn và slettes tự động sau khi xử lý. Chúng tôi không lưu trữ tệp của bạn lâu dài.',
    footer_tools_title: 'Công cụ và Trang',
    footer_support_title: 'Sử hữu và Pháp luật',
    footer_copyright: 'Công cụ trích xuất và xóa âm thanh online miễn phí.',
    footer_all_rights: 'Tất cả các quyền được bảo lưu.',
    footer_popular_searches_label: 'Các tìm kiếm phổ biến:',
    footer_popular_searches: 'video sang mp3 miễn phí · extrator audio online miễn phí · xóa âm thanh khỏi video · audio remover miễn phí',
    lang_modal_title: 'Chọn ngôn ngữ',
    lang_modal_count: 'ngôn ngữ có sẵn',
    lang_search_placeholder: 'Tìm kiếm ngôn ngữ...',
    lang_no_results: 'Không có ngôn ngữ nào phù hợp',
  },
  vi: {
    nav_home: 'Trang chủ',
    nav_extractor: 'Trích xuất âm thanh',
    nav_remover: 'Xóa âm thanh',
    nav_help: 'Trợ giúp',
    nav_contact: 'Liên hệ',
    hero_badge_instant: 'Xử lý tức thì',
    hero_badge_deleted: 'Tệp tự động xóa',
    hero_badge_free: 'Miễn phí mãi mãi',
    hero_title_1: 'Trích xuất âm thanh từ',
    hero_title_2: 'bất kỳ video nào',
    hero_title_3: 'ngay lập tức',
    hero_subtitle: 'Chuyển đổi video sang MP3, WAV, FLAC và nhiều hơn nữa với công cụ trích xuất âm thanh trực tuyến miễn phí. Hoặc xóa hoàn toàn âm thanh khỏi bất kỳ video nào — không cần tài khoản.',
    hero_tab_extractor: 'Trích xuất âm thanh',
    hero_tab_remover: 'Xóa âm thanh',
    formats_title: 'Định dạng được hỗ trợ',
    formats_subtitle: 'Tất cả các định dạng đầu vào video phổ biến và 9 định dạng output audio',
    formats_video_title: 'Định dạng đầu vào video',
    formats_video_subtitle: 'Tải lên video theo các định dạng này',
    formats_audio_title: 'Định dạng output audio',
    formats_audio_subtitle: 'Trích xuất thành một trong số các định dạng này',
    formats_max_size: 'Kích thước tệp tối đa:',
    formats_popular: 'PHỔ BIẾN',
    faq_badge: 'Câu hỏi thường gặp',
    faq_title: 'Tất cả những gì bạn cần biết',
    faq_subtitle: 'Câu hỏi phổ biến về trích xuất âm thanh, xóa âm thanh và định dạng tệp',
    faq_cat_all: 'Tất cả',
    footer_desc: 'Công cụ online miễn phí để trích xuất âm thanh từ video và xóa âm thanh khỏi tệp video nhanh và dễ dàng. Không cần tài khoản. Không có hình mờ. Không giới hạn.',
    footer_output_formats: 'Các định dạng đầu ra được hỗ trợ',
    footer_privacy_notice_title: 'Quyền riêng tư của bạn được bảo vệ.',
    footer_privacy_notice: 'Tất cả các tệp được tải lên được xử lý an toàn và slettes tự động sau khi xử lý. Chúng tôi không lưu trữ tệp của bạn lâu dài.',
    footer_tools_title: 'Công cụ và Trang',
    footer_support_title: 'Sử hữu và Pháp luật',
    footer_copyright: 'Công cụ trích xuất và xóa âm thanh online miễn phí.',
    footer_all_rights: 'Tất cả các quyền được bảo lưu.',
    footer_popular_searches_label: 'Các tìm kiếm phổ biến:',
    footer_popular_searches: 'video sang mp3 miễn phí · extrator audio online miễn phí · xóa âm thanh khỏi video · audio remover miễn phí',
    lang_modal_title: 'Chọn ngôn ngữ',
    lang_modal_count: 'ngôn ngữ có sẵn',
    lang_search_placeholder: 'Tìm kiếm ngôn ngữ...',
    lang_no_results: 'Không có ngôn ngữ nào phù hợp',
  },
  'zh-tw': {
    nav_home: '首頁',
    nav_extractor: '音頻提取器',
    nav_remover: '音頻移除器',
    nav_help: '幫助',
    nav_contact: '聯絡我們',
    hero_badge_instant: '即時處理',
    hero_badge_deleted: '檔案自動刪除',
    hero_badge_free: '永久免費',
    hero_title_1: '從',
    hero_title_2: '任何影片',
    hero_title_3: '中提取音頻',
    hero_subtitle: '使用我們的免費線上音頻提取器將影片轉換為MP3、WAV、FLAC等格式。或從任何影片中完全刪除音頻——無需帳戶。',
    hero_tab_extractor: '音頻提取器',
    hero_tab_remover: '音頻移除器',
    formats_title: '支援的格式',
    formats_subtitle: '支援所有流行的影片輸入格式和9種音頻輸出格式',
    formats_video_title: '影片輸入格式',
    formats_video_subtitle: '以這些格式上傳影片',
    formats_audio_title: '音頻輸出格式',
    formats_audio_subtitle: '提取為以下任意格式',
    formats_max_size: '最大檔案大小：',
    formats_popular: '熱門',
    faq_badge: '常見問題',
    faq_title: '您需要了解的一切',
    faq_subtitle: '關於音頻提取、音頻移除和檔案格式的常見問題',
    faq_cat_all: '全部',
    footer_desc: '免費線上工具，快速輕鬆地從影片中提取音頻並從影片檔案中刪除音頻。無需帳戶。無水印。限制亦無。',
    footer_output_formats: '支援的輸出格式',
    footer_privacy_notice_title: '您的隱私受到保護。',
    footer_privacy_notice: '所有上傳的檔案都經過安全處理，並在處理後自動刪除。我們從不長期存儲您的檔案。',
    footer_tools_title: '工具和頁面',
    footer_support_title: '支援及法律',
    footer_copyright: '免費線上音頻提取器和移除器。',
    footer_all_rights: '版權所有。',
    footer_popular_searches_label: '熱門搜尋：',
    footer_popular_searches: '影片轉mp3免費 · 線上音頻提取器免費 · 從影片中刪除音頻 · 音頻移除器免費',
    lang_modal_title: '選擇語言',
    lang_modal_count: '種語言可用',
    lang_search_placeholder: '搜尋語言...',
    lang_no_results: '沒有語言匹配',
  },
  'zh-tw': {
    nav_home: '首頁',
    nav_extractor: '音頻提取器',
    nav_remover: '音頻移除器',
    nav_help: '幫助',
    nav_contact: '聯絡我們',
    hero_badge_instant: '即時處理',
    hero_badge_deleted: '檔案自動刪除',
    hero_badge_free: '永久免費',
    hero_title_1: '從',
    hero_title_2: '任何影片',
    hero_title_3: '中提取音頻',
    hero_subtitle: '使用我們的免費線上音頻提取器將影片轉換為MP3、WAV、FLAC等格式。或從任何影片中完全刪除音頻——無需帳戶。',
    hero_tab_extractor: '音頻提取器',
    hero_tab_remover: '音頻移除器',
    formats_title: '支援的格式',
    formats_subtitle: '支援所有流行的影片輸入格式和9種音頻輸出格式',
    formats_video_title: '影片輸入格式',
    formats_video_subtitle: '以這些格式上傳影片',
    formats_audio_title: '音頻輸出格式',
    formats_audio_subtitle: '提取為以下任意格式',
    formats_max_size: '最大檔案大小：',
    formats_popular: '熱門',
    faq_badge: '常見問題',
    faq_title: '您需要了解的一切',
    faq_subtitle: '關於音頻提取、音頻移除和檔案格式的常見問題',
    faq_cat_all: '全部',
    footer_desc: '免費線上工具，快速輕鬆地從影片中提取音頻並從影片檔案中刪除音頻。無需帳戶。無水印。限制亦無。',
    footer_output_formats: '支援的輸出格式',
    footer_privacy_notice_title: '您的隱私受到保護。',
    footer_privacy_notice: '所有上傳的檔案都經過安全處理，並在處理後自動刪除。我們從不長期存儲您的檔案。',
    footer_tools_title: '工具和頁面',
    footer_support_title: '支援及法律',
    footer_copyright: '免費線上音頻提取器和移除器。',
    footer_all_rights: '版權所有。',
    footer_popular_searches_label: '熱門搜尋：',
    footer_popular_searches: '影片轉mp3免費 · 線上音頻提取器免費 · 從影片中刪除音頻 · 音頻移除器免費',
    lang_modal_title: '選擇語言',
    lang_modal_count: '種語言可用',
    lang_search_placeholder: '搜尋語言...',
    lang_no_results: '沒有語言匹配',
  },
  ar: {
    nav_home: 'الرئيسية',
    nav_extractor: 'استخراج الصوت',
    nav_remover: 'إزالة الصوت',
    nav_help: 'مساعدة',
    nav_contact: 'اتصل بنا',
    hero_badge_instant: 'معالجة فورية',
    hero_badge_deleted: 'الملفات تُحذف تلقائياً',
    hero_badge_free: 'مجاني للأبد',
    hero_title_1: 'استخرج الصوت من',
    hero_title_2: 'أي فيديو',
    hero_title_3: 'فوراً',
    hero_subtitle: 'حوّل الفيديو إلى MP3 وWAV وFLAC والمزيد باستخدام أداة استخراج الصوت المجانية عبر الإنترنت. أو احذف الصوت كلياً من أي فيديو — بدون الحاجة إلى حساب.',
    hero_tab_extractor: 'استخراج الصوت',
    hero_tab_remover: 'إزالة الصوت',
    formats_title: 'الصيغ المدعومة',
    formats_subtitle: 'جميع صيغ إدخال الفيديو الشائعة و9 صيغ إخراج صوتية مدعومة',
    formats_video_title: 'صيغ إدخال الفيديو',
    formats_video_subtitle: 'ارفع مقاطع الفيديو بهذه الصيغ',
    formats_audio_title: 'صيغ إخراج الصوت',
    formats_audio_subtitle: 'Extrahujte do dowolnego z tych formátów',
    formats_max_size: 'Dimensiunea maximă a fișierului:',
    formats_popular: 'ПОПУЛЯРНОЕ',
    faq_badge: 'Часто Задаваемые Вопросы',
    faq_title: 'Всё, что вам нужно знать',
    faq_subtitle: 'Частные вопросы об извлечении аудио, удалении аудио и форматах файлов',
    faq_cat_all: 'Всё',
    footer_desc: 'Бесплатные онлайн-инструменты для извлечения аудио из видеoclipов и для удаления аудио из видеофайлов быстро и просто. Без регистрации. Без водяных знаков. Без ограничений.',
    footer_output_formats: 'Підтримувані Формати Виводу',
    footer_privacy_notice_title: 'Ваша конфіденційність захищена.',
    footer_privacy_notice: 'Toate fișierele încărcate sunt procesate în siguranță și șterse automat după procesare. Nu stocăm niciodată fișierele tale pe termen lung.',
    footer_tools_title: 'Інструменти та Сторінки',
    footer_support_title: 'Підтримка та Правова Інформація',
    footer_copyright: 'Безкоштовний онлайн-витягувач та видалювач аудіо.',
    footer_all_rights: 'Всі права захищені.',
    footer_popular_searches_label: 'Популярні запити:',
    footer_popular_searches: 'відео в mp3 безкоштовно · онлайн витяг аудіо безкоштовно · elimină sunet din video · eliminator sunet gratuit',
    lang_modal_title: 'اختر اللغة',
    lang_modal_count: 'لغة متاحة',
    lang_search_placeholder: 'ابحث عن لغة...',
    lang_no_results: 'لا توجد لغات مطابقة',
  },
  ro: {
    nav_home: 'Acasă',
    nav_extractor: 'Extractor Audio',
    nav_remover: 'Eliminator Audio',
    nav_help: 'Ajutor',
    nav_contact: 'Contact',
    hero_badge_instant: 'Procesare Instantanee',
    hero_badge_deleted: 'Fișiere Șterse Automat',
    hero_badge_free: 'Gratuit Pentru Totdeauna',
    hero_title_1: 'Extrage Audio Din',
    hero_title_2: 'Orice Video',
    hero_title_3: 'Instantaneu',
    hero_subtitle: 'Convertește video în MP3, WAV, FLAC și mai mult cu extractorul nostru audio gratuit online. Sau elimină audio complet din orice video — fără cont.',
    hero_tab_extractor: 'Extractor Audio',
    hero_tab_remover: 'Eliminator Audio',
    formats_title: 'Formate Suportate',
    formats_subtitle: 'Toate formatele video de intrare populare și 9 formate de ieșire audio',
    formats_video_title: 'Formaturi de Intrare Video',
    formats_video_subtitle: 'Încarcă videoclipuri în aceste formate',
    formats_audio_title: 'Formaturi de Ieșire Audio',
    formats_audio_subtitle: 'Extrage în oricare din aceste formate',
    formats_max_size: 'Dimensiunea maximă a fișierului:',
    formats_popular: 'POPULAR',
    faq_badge: 'Întrebări Frecvente',
    faq_title: 'Tot ce trebuie să știți',
    faq_subtitle: 'Întrebări comune despre extragerea audio, eliminarea audio și formatele de fișiere',
    faq_cat_all: 'Toate',
    footer_desc: 'Instrumente online gratuite pentru a extrage audio din videoclipuri și pentru a elimina audio din fișiere video rapid și simplu. Fără cont. Fără filigran. Fără limite.',
    footer_output_formats: 'Formate de ieșire suportate',
    footer_privacy_notice_title: 'Confidențialitatea ta este protejată.',
    footer_privacy_notice: 'Toate fișierele încărcate sunt procesate în siguranță și șterse automat după procesare. Nu stocăm niciodată fișierele tale pe termen lung.',
    footer_tools_title: 'Instrumente și Pagini',
    footer_support_title: 'Suport și Legal',
    footer_copyright: 'Extractor și eliminator audio online gratuite.',
    footer_all_rights: 'Toate drepturile rezervate.',
    footer_popular_searches_label: 'Căutări populare:',
    footer_popular_searches: 'video în mp3 gratuit · extractor audio online gratuit · elimină sunet din video · eliminator sunet gratuit',
    lang_modal_title: 'Alege Limba',
    lang_modal_count: 'limbi disponibile',
    lang_search_placeholder: 'Caută limbi...',
    lang_no_results: 'Nici o limbă nu se potrivește',
  },
};

// RTL languages
const RTL_LANGS: LangCode[] = ['ar', 'fa', 'ur'];

interface LanguageContextType {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  t: (key: keyof Translations) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key) => translations.en[key],
  isRTL: false,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LangCode>('en');

  useEffect(() => {
    const saved = localStorage.getItem('preferred_lang') as LangCode | null;
    if (saved && translations[saved]) {
      setLangState(saved);
    }
  }, []);

  const setLang = (newLang: LangCode) => {
    setLangState(newLang);
    localStorage.setItem('preferred_lang', newLang);
  };

  const t = (key: keyof Translations): string => {
    return translations[lang]?.[key] ?? translations.en[key] ?? key;
  };

  const isRTL = RTL_LANGS.includes(lang);

  return (
    <LanguageContext.Provider data-component-id="src/context/LanguageContext.tsx:775:4" data-component-path="src/context/LanguageContext.tsx" data-component-line="775" data-component-end-line="777" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ lang, setLang, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  return useContext(LanguageContext);
}